import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { setAuthToken, getAuthToken, setUser as saveUser, getUser as getSavedUser, logout as clearAuth } from '../utils/auth';
import analytics from '../utils/analytics';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      const savedUser = getSavedUser();

      console.log('[AuthContext] Initializing auth:', { hasToken: !!token, hasUser: !!savedUser });

      if (token && savedUser) {
        setUser(savedUser);
        console.log('[AuthContext] User loaded from localStorage:', savedUser.username);

        // Verify token is still valid by fetching current user
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data.user);
          saveUser(response.data.user);
          console.log('[AuthContext] Token verified, user updated from API');
        } catch (err) {
          // Token is invalid, clear auth
          console.error('[AuthContext] Token verification failed:', err.message);
          clearAuth();
          setUser(null);
        }
      } else {
        console.log('[AuthContext] No token or user in localStorage');
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const register = async (payload) => {
    try {
      setError(null);
      const response = await authAPI.register(payload);

      // Check if verification is required
      if (response.data.requiresVerification) {
        analytics.signUp('email_verification_required');
        return {
          success: true,
          requiresVerification: true,
          user: response.data.user
        };
      }

      // Old flow - direct login (backwards compatibility)
      const { token, user: userData } = response.data;
      setAuthToken(token);
      saveUser(userData);
      setUser(userData);

      analytics.signUp('email');

      return { success: true, user: userData };
    } catch (err) {
      const errorMessage = err.response?.data?.error ||
                           err.response?.data?.errors?.[0]?.msg ||
                           'Registration failed';
      setError(errorMessage);
      analytics.authError('registration_failed');
      return { success: false, error: errorMessage };
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { token, user: userData } = response.data;

      setAuthToken(token);
      saveUser(userData);
      setUser(userData);

      analytics.login('email');

      return { success: true, user: userData };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      const errorData = err.response?.data || {};

      setError(errorMessage);
      analytics.authError('login_failed');

      return {
        success: false,
        error: errorMessage,
        requiresVerification: errorData.requiresVerification,
        verificationMethod: errorData.verificationMethod,
        contact: errorData.contact,
      };
    }
  };

  const logout = () => {
    analytics.logout();
    clearAuth();
    setUser(null);
    setError(null);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data.user);
      saveUser(response.data.user);
      console.log('[AuthContext] User refreshed from API');
    } catch (err) {
      console.error('[AuthContext] Failed to refresh user:', err.message);
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
